import { useEffect, useState } from 'react';
import { fetchCourses } from '@/services/learn/course.service';
import type { Course, LearnPage, UploadFile } from '@edwin-group/people-graphql';
import CoursesSkeleton, { HighlightSkeleton } from './Courses.skeleton';
import Container from '@/components/Container';
import Navigation, { NavigationSkeleton } from '@/components/ui/navigation';
import { navigationLearn } from '../constants';

import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';
import { getPage } from '@/services/learn/learn.service';
import LessonCard from '@/components/Learn/Lesson';
import Highlight from '@/components/Highlight';
import { BackButton } from '@/components/ui/back-button';

function CoursesTab() {
  const [courses, setCourses] = useState<Array<Course> | null>(null);
  const [learnPage, setLearnPage] = useState<LearnPage | null>(null);

  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const coursesResult = await fetchCourses();
      const pageResponse = await getPage();

      setCourses(coursesResult);
      setLearnPage(pageResponse.page);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return (
      <>
        <HighlightSkeleton />
        <NavigationSkeleton />
        <Container className="last-divider-hidden mt-sh-60 min-h-40">
          <CoursesSkeleton />
        </Container>
      </>
    );
  }

  return (
    <>
      {learnPage?.learnHighlight && (learnPage.learnHighlight.title || learnPage.learnHighlight.description) ? (
        <Highlight
          title={learnPage.learnHighlight.title ?? ''}
          sectionTitle="Learn"
          description={learnPage.learnHighlight.description ?? ''}
          image={learnPage.learnHighlight.image?.data?.attributes?.url}
          imageAlt={
            learnPage.learnHighlight.image?.data?.attributes?.alternativeText ??
            learnPage.learnHighlight.title ??
            'Learn section'
          }
          backButton={<BackButton to="/" label="Back" />}
        />
      ) : (
        <Container>
          <div className="mb-sh-30 flex flex-col pl-sh-15">
            <BackButton to="/" label="Back" />
          </div>
        </Container>
      )}
      <Navigation title="Learn" items={navigationLearn} />
      <Container className="last-divider-hidden mt-sh-30 min-h-40">
        {!courses || courses.length === 0 ? (
          <div className="col-span-12 mt-8 text-center">{learnPage?.emptyCoursesMessage || `No courses available`}</div>
        ) : (
          <div className="col-span-12 last:mb-0">
            <div className="flex flex-col gap-sh-30">
              {courses.map((course) => {
                const courseTitle = course?.title ?? course?.attributes?.title ?? '';
                const courseDescription = course?.attributes?.description ?? '';
                const courseSlug = course?.attributes?.slug ?? course?.slug ?? '';
                const courseDuration = course?.attributes?.duration ?? '';

                const thumbnailData = course?.attributes?.thumbnail?.data?.attributes;
                const thumbnail = thumbnailData
                  ? ({
                      url: thumbnailData.url,
                      alternativeText: thumbnailData.alternativeText ?? '',
                    } as UploadFile)
                  : undefined;

                return (
                  <AnimatedFadeInComponent key={courseTitle || course?.documentId}>
                    <LessonCard
                      title={courseTitle}
                      description={courseDescription}
                      thumbnail={thumbnail}
                      url={`/learn/course/${courseSlug}`}
                      duration={courseDuration}
                      tag="Course"
                    />
                  </AnimatedFadeInComponent>
                );
              })}
            </div>
          </div>
        )}
      </Container>
    </>
  );
}

export default CoursesTab;
